import { csvParse } from 'd3';
import { Plot } from 'd3plus';

export const initChartWithToggle = () => {
  // Only proceed if chart-switcher exists
  if (document.querySelector('.market-growth-chart') === null) {
    return;
  }

  let titleFontSize = 10;
  let xFontSize = 10;
  let quarterData;
  let yearlyData;
  const switcherParent = document.querySelector('.chart-switcher');
  const switcherInput = switcherParent.querySelector('input');
  const overlay = switcherParent.querySelector('.toggler');
  const switchVal = switcherParent.querySelectorAll('.switcher-val');

  // Handle flexible number of switcher values
  const quarterDataPath =
    switchVal.length > 0 ? switchVal[0].dataset.src : null;
  const yearlyDataDataPath =
    switchVal.length > 1 ? switchVal[1].dataset.src : null;
  let counter = 0;
  const dataKeys = ['IYR ETF', 'S&P 500', 'NCREIF Property Index Returns'];
  /** @type {Plot} */
  let chart;

  const generateData = (array, keys, type) =>
    array.flatMap((item) =>
      keys.map((key) => {
        const yValue = item[key];
        return {
          id: key,
          x:
            type === 'yearly'
              ? item.Quarter.slice(0, item.Quarter.length - 3)
              : item.Quarter,
          y: yValue.includes('%') ? yValue.slice(0, -1) : yValue,
        };
      }),
    );

  const setData = (data, range) => {
    if (data) {
      if (range === 'yearly') {
        const result = csvParse(data);
        delete result.columns;
        yearlyData = generateData(result, dataKeys, range);
      } else {
        const result = csvParse(data);
        delete result.columns;
        quarterData = generateData(result, dataKeys);
      }
    }
  };

  const initChart = () => {
    const initialData = quarterData || yearlyData;
    if (!initialData) {
      return;
    }

    chart = new Plot()
      .data(initialData)
      .groupBy('id')
      .discrete('x')
      .shape('Line')
      .select('.chart-inner')
      .groupPadding(0)
      .x((d) => {
        return d.x;
      })
      .y((d) => {
        return Number(d.y);
      })
      .shapeConfig({
        Line: {
          stroke(d) {
            if (d.id === 'S&P 500') {
              return '#4EE5CC';
            }
            if (d.id === 'IYR ETF') {
              return '#007395';
            }
            if (d.id === 'NCREIF Property Index Returns') {
              return '#1D2328';
            }

            return '#1D2328';
          },
        },
      })
      .legendPosition('bottom')
      .legendConfig({
        align: 'left',
        shape: 'Circle',
        shapeConfig: {
          labelConfig: {
            fontColor: '#1D2328',
            fontMax: 12,
          },
          r() {
            return 9;
          },
        },
        padding: 5,
      })
      .xConfig({
        gridConfig: {
          stroke: '#1d23001a',
        },
        labelRotation: true,
        shapeConfig: {
          fill: '#fff',
          stroke: '#fff',
          labelConfig: {
            fontColor() {
              if (window.innerWidth < 568 && this.data().length > 30) {
                counter += 1;
                if (counter % 2 === 0 || counter % 3) {
                  return 'rgba(255,255,255,0)';
                }
                return '#1D2328';
              }
              if (this.data().length > 100) {
                counter += 1;
                if (counter % 2 === 0) {
                  return 'rgba(255,255,255,0)';
                }
                return '#1D2328';
              }
              return '#1D2328';
            },
            fontSize() {
              return xFontSize;
            },
          },
        },
        barConfig: {
          stroke: '#1d23001a',
        },
      })
      .yConfig({
        gridConfig: {
          stroke: '#1d23001a',
        },
        shapeConfig: {
          fill: '#fff',
          stroke: '#fff',
          labelConfig: {
            fontColor: '#1D2328',
            fontSize() {
              return xFontSize;
            },
          },
        },
        barConfig: {
          stroke: '#1d23001a',
        },
        title: 'Growth (%)',
        titleConfig: {
          fontColor: '#1D2328',
          fontSize: titleFontSize,
        },
      })
      .attributionStyle({
        font: "400 10px/10px 'Grotesk', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
        padding: '0 0 0',
      })
      .colorScaleMaxSize(400)
      .tooltip(false)
      .render();
  };

  // Responsive font sizes
  if (window.innerWidth > 767) {
    titleFontSize = 14;
    xFontSize = 12;
  }

  // Only proceed if we have at least one data path
  if (!quarterDataPath && !yearlyDataDataPath) {
    return;
  }

  // Load yearly data if available
  if (yearlyDataDataPath) {
    fetch(yearlyDataDataPath)
      .then((res) => res.text())
      .then((data) => {
        setData(data, 'yearly');
        initChart();
      });
  }

  // Load quarterly data and initialize chart
  if (quarterDataPath) {
    fetch(quarterDataPath)
      .then((res) => res.text())
      .then((data) => {
        setData(data);
        initChart();
      });
  } else if (yearlyDataDataPath) {
    // If only yearly data is available, use it to initialize the chart
    fetch(yearlyDataDataPath)
      .then((res) => res.text())
      .then((data) => {
        setData(data, 'yearly');
        initChart();
      });
  }

  // Toggle switcher functionality - only if we have both datasets
  if (switchVal.length > 1 && quarterData && yearlyData) {
    switcherInput.addEventListener('change', (e) => {
      if (e.target.checked) {
        switcherParent.classList.add('changed');
        overlay.style.width = `${switchVal[1].offsetWidth}px`;
        overlay.style.left = `calc(100% - ${switchVal[1].offsetWidth}px)`;

        chart
          .select()
          .groups[0].querySelector('.d3plus-plot-x-axis .grid > line')
          .remove();
        counter = 0;
        chart.data(yearlyData).render();
      } else {
        switcherParent.classList.remove('changed');
        overlay.style.width = `${switchVal[0].offsetWidth}px`;
        overlay.style.left = '0';

        chart
          .select()
          .groups[0].querySelector('.d3plus-plot-x-axis .grid > line')
          .remove();
        counter = 0;
        chart.data(quarterData).render();
      }
    });
  }
};
